import { useMemo } from "react"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { TrackingProperties } from "connected-components/analytics"
import {
  DEFAULT_ASSET_VIEW_STATE,
  ASSET_VIEW_PREFERENCE_BASE,
} from "connected-components/dashboard/store/asset-view-slice"

/**
 * Tracked properties for the "Asset View" analytics scope. Extends TrackingProperties
 * (`Record<string, unknown>`) — same pattern as AnalyticsContextProperties — so it carries the
 * index signature the provider's props sink requires while still naming the known fields.
 */
export interface AssetViewAnalyticsProperties extends TrackingProperties {
  view: string
  groupBy: string | undefined
  showNotHeld: boolean | null
  showMetricsSelector: boolean
  navigationPath: string
  navigationDepth: number
}

/**
 * Panel-scoped asset-view analytics properties, read directly from the RTK slice.
 *
 * Reads only the asset-view slice slot — not the full useAssetViewData value — so it doesn't run
 * core/trade/model selectors or rebuild action creators just to pick ~7 fields. Dashboard
 * properties (modelId, instrumentId, etc.) are inherited through provider nesting and must NOT be
 * extracted here.
 *
 * @returns Memoized asset view analytics properties
 */
export function useAssetViewAnalyticsProperties(): AssetViewAnalyticsProperties {
  const dashboardId = useDashboardId()
  const panelId = usePanelId() ?? ASSET_VIEW_PREFERENCE_BASE
  const state = useAppSelector(
    (s) => s.dashboard.assetView[dashboardId]?.[panelId] ?? DEFAULT_ASSET_VIEW_STATE,
  )

  const { view, groupBy, showNotHeld, showMetricsSelector, selectedTableLevels } = state
  const groupByLabel = groupBy?.label
  const groupById = groupBy?.id
  const navigationPath = selectedTableLevels.join("-")
  const navigationDepth = selectedTableLevels.length

  // Memoize on primitives only so the tracked-properties object stays referentially stable
  // across unrelated slice writes (e.g. datagridState changes).
  return useMemo<AssetViewAnalyticsProperties>(
    () => ({
      view,
      groupBy: groupByLabel || groupById,
      showNotHeld,
      showMetricsSelector,
      navigationPath,
      navigationDepth,
    }),
    [view, groupByLabel, groupById, showNotHeld, showMetricsSelector, navigationPath, navigationDepth],
  )
}
